import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { SpanCol } from "skedyul-ui/ui";
import { store } from "@store/index";
import { updateParams } from "@store/features/layout/sideNavigationSlice";
import { open as openDialog } from "@store/features/layout/dialogSlice";

type MigrationOperation = {
  id: string;
  operationType: string;
  resourceType: string;
  resourceHandle: string;
  isDestructive: boolean;
  impactSummary: string | null;
  status: string;
  executedAt: string | null;
};

type Migration = {
  id: string;
  source: string;
  status: string;
  requiresApproval: boolean;
  createdAt: string;
  completedAt: string | null;
  startedAt: string | null;
  errorMessage: string | null;
  operations: MigrationOperation[];
};

type MigrationHistoryParams = {
  migrations?: Migration[];
  isLoading?: boolean;
  hasMore?: boolean;
};

const getStatusVariant = (
  status: string,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "COMPLETED":
      return "default";
    case "FAILED":
    case "ROLLED_BACK":
      return "destructive";
    case "PENDING":
    case "BATCHING":
    case "EXECUTING":
      return "secondary";
    case "AWAITING_APPROVAL":
    case "APPROVED":
      return "outline";
    default:
      return "secondary";
  }
};

const getOperationTypeLabel = (operationType: string): string => {
  switch (operationType) {
    case "CREATE_MODEL":
      return "Create Model";
    case "UPDATE_MODEL":
      return "Update Model";
    case "DELETE_MODEL":
      return "Delete Model";
    case "CREATE_FIELD":
      return "Create Field";
    case "UPDATE_FIELD":
      return "Update Field";
    case "DELETE_FIELD":
      return "Delete Field";
    case "CREATE_RELATIONSHIP":
      return "Create Relationship";
    case "UPDATE_RELATIONSHIP":
      return "Update Relationship";
    case "DELETE_RELATIONSHIP":
      return "Delete Relationship";
    default:
      return operationType.replace(/_/g, " ");
  }
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

const summarizeOperations = (operations: MigrationOperation[]): string => {
  if (!operations || operations.length === 0) {
    return "No operations";
  }

  const labels = operations.map((op) =>
    getOperationTypeLabel(op.operationType),
  );

  if (labels.length <= 3) {
    return labels.join(", ");
  }

  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`;
};

export const migrationHistorySchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: MigrationHistoryParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation } = args.state;
  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  const params = sideNavigation[args.position]
    ?.params as MigrationHistoryParams;
  const migrations = params?.migrations || [];
  const isLoading = params?.isLoading ?? true;

  const emptyState =
    migrations.length === 0 && !isLoading
      ? [
          {
            type: "Card" as const,
            title: "No migrations",
            props: {
              variant: "outline" as const,
              header: {
                title: "No migrations yet",
                description:
                  "Migrations will appear here when you make changes to your CRM schema, such as creating models, adding fields, or modifying relationships.",
              },
            },
          },
        ]
      : [];

  const loadingState = isLoading
    ? [
        {
          type: "Card" as const,
          title: "Loading",
          props: {
            variant: "outline" as const,
            header: {
              title: "Loading migrations...",
              description: "Please wait while we fetch your migration history.",
            },
          },
        },
      ]
    : [];

  const dataTableComponent =
    migrations.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Migrations",
            props: {
              variant: "outline" as const,
              form: {
                formVersion: "v1" as const,
                fields: [
                  {
                    component: "DataTable" as const,
                    span: 12 as SpanCol,
                    mdSpan: 12 as SpanCol,
                    lgSpan: 12 as SpanCol,
                    props: {
                      data: migrations,
                      emptyMessage: "No migrations found",
                      columns: [
                        {
                          id: "operations",
                          type: "rowLayout" as const,
                          header: "Operations",
                          accessorKey: "operations",
                          align: "left" as const,
                          components: [
                            {
                              component: "Text" as const,
                              generateProps: (
                                row: Migration,
                                _index: number,
                              ) => ({
                                children: summarizeOperations(row.operations),
                              }),
                            },
                          ],
                        },
                        {
                          id: "status",
                          type: "rowLayout" as const,
                          header: "Status",
                          accessorKey: "status",
                          align: "center" as const,
                          components: [
                            {
                              component: "Badge" as const,
                              generateProps: (
                                row: Migration,
                                _index: number,
                              ) => ({
                                children: row.status.replace(/_/g, " "),
                                variant: getStatusVariant(row.status),
                                size: "sm" as const,
                              }),
                            },
                          ],
                        },
                        {
                          id: "timestamp",
                          type: "rowLayout" as const,
                          header: "When",
                          accessorKey: "createdAt",
                          align: "right" as const,
                          components: [
                            {
                              component: "Text" as const,
                              generateProps: (
                                row: Migration,
                                _index: number,
                              ) => ({
                                children: formatRelativeTime(row.createdAt),
                                className: "text-muted-foreground text-sm",
                              }),
                            },
                          ],
                        },
                      ],
                      onRowClick: (row: Migration) => {
                        store.dispatch(
                          openDialog({
                            schema: "MIGRATION_DETAIL",
                            params: {
                              migration: row,
                            },
                          }),
                        );
                      },
                    },
                  },
                ],
              },
            },
          },
        ]
      : [];

  return {
    side: args.position,
    isOpen,
    width: args.width || "600px",
    component: {
      component: "PageV2",
      props: {
        title: "Migration History",
        hideTitle: false,
        header: {
          icon: "History",
        },
        content: {
          structure: "sidebar",
          components: [...loadingState, ...emptyState, ...dataTableComponent],
        },
      },
    },
  };
};

export const fetchMigrationHistory = async (position: "left" | "right") => {
  try {
    store.dispatch(
      updateParams({
        position,
        params: { isLoading: true, migrations: [] },
      }),
    );

    const { trpc } = await import("@utils/api");
    const migrations = await trpc.crm.getMigrations.query({
      limit: 50,
      offset: 0,
    });

    store.dispatch(
      updateParams({
        position,
        params: {
          isLoading: false,
          migrations: migrations as unknown as Migration[],
        },
      }),
    );
  } catch (error) {
    console.error("Failed to fetch migration history:", error);
    store.dispatch(
      updateParams({
        position,
        params: { isLoading: false, migrations: [] },
      }),
    );
  }
};
